import type { ComponentType, SVGProps } from 'react';

import {
  RMFindReviewsParamsSchema,
  RMUpdateReviewSettingsSchema,
  type RMFindReviewsParams
} from '@brightlocal/api-client';
import { FacebookOriginal, GoogleOriginal, TrustpilotOriginal, YelpOriginal } from '@brightlocal/icons';
import {
  RM_REVIEW_RATING_FILTER,
  RM_REVIEW_STATUS,
  type RmDirectoryIdentifier,
  type RmReviewRatingFilter,
  type RmReviewStatus
} from '@brightlocal/shared';
import type { BadgeProps } from '@brightlocal/ui-components/badge';

import { REVIEW_PERIOD, REVIEW_REPORT_DATA_QUERY_KEYS } from 'shared/modules/reviews';

import type { ReviewsFilters } from './types';

/** Query keys of the Review Manager requests. */
export const REVIEW_MANAGER_QUERY_KEYS = {
  REVIEWS: REVIEW_REPORT_DATA_QUERY_KEYS.MANAGER_REVIEWS,
  ALL_REVIEWS_COUNT: REVIEW_REPORT_DATA_QUERY_KEYS.MANAGER_ALL_REVIEWS_COUNT,
  RUN_STATUS: 'reviewManagerRunStatus',
  UPDATE_REVIEW_STATUS: 'reviewManagerUpdateReviewStatus'
} as const;

/** Mutation key of a review status change, so a row can tell that its own change is on its way. */
export const UPDATE_REVIEW_STATUS_MUTATION_KEY = [REVIEW_MANAGER_QUERY_KEYS.UPDATE_REVIEW_STATUS] as const;

/** Query keys a finished run changes at once: the report's date and Review Tracker's data. */
export const RUN_REFRESHED_QUERY_KEYS = [
  REVIEW_REPORT_DATA_QUERY_KEYS.REPORT,
  REVIEW_REPORT_DATA_QUERY_KEYS.TRACKER_PERFORMANCE,
  REVIEW_REPORT_DATA_QUERY_KEYS.TRACKER_DIRECTORY_BREAKDOWN
] as const;

/** Query keys a finished run changes once {@link RUN_SETTLE_DELAY_MS} has passed: they read the review search. */
export const RUN_SETTLED_QUERY_KEYS = [
  REVIEW_MANAGER_QUERY_KEYS.REVIEWS,
  REVIEW_MANAGER_QUERY_KEYS.ALL_REVIEWS_COUNT
] as const;

/**
 * How long the page waits after a run ends before it reads the review search again: the search may not hold the
 * run's reviews the moment the run ends, so an earlier read could show "no reviews" over reviews that exist.
 */
export const RUN_SETTLE_DELAY_MS = 3_000;

/**
 * How long the inbox waits after the last write to a review before it reads the list again: the review search catches
 * up a second or two after a write, so an earlier read could bring the old status back.
 */
export const WRITE_SETTLE_DELAY_MS = 3_000;

/** The inbox with no search and no filter: every review of the open tab. The page address leaves these values out. */
export const REVIEWS_DEFAULT_FILTERS = {
  query: '',
  sites: [],
  ratings: [],
  period: REVIEW_PERIOD.ALL_TIME
} as const satisfies ReviewsFilters;

const { FIVE, FOUR, THREE, TWO, ONE, RECOMMENDED, NOT_RECOMMENDED, NO_RATING } = RM_REVIEW_RATING_FILTER;

/** The Rating filter's options in the order it lists them: 5 to 1 star, then the recommendations, then no rating. */
export const REVIEW_RATING_FILTER_ORDER = [
  FIVE,
  FOUR,
  THREE,
  TWO,
  ONE,
  RECOMMENDED,
  NOT_RECOMMENDED,
  NO_RATING
] as const;

/** Label key of each Rating filter option; the recommendations and no rating read as the rating cell does. */
export const REVIEW_RATING_FILTER_LABEL_KEYS = {
  [FIVE]: 'reports.reviewManager.filters.rating.five',
  [FOUR]: 'reports.reviewManager.filters.rating.four',
  [THREE]: 'reports.reviewManager.filters.rating.three',
  [TWO]: 'reports.reviewManager.filters.rating.two',
  [ONE]: 'reports.reviewManager.filters.rating.one',
  [RECOMMENDED]: 'reports.reviewManager.reviews.rating.recommended',
  [NOT_RECOMMENDED]: 'reports.reviewManager.reviews.rating.notRecommended',
  [NO_RATING]: 'reports.reviewManager.reviews.rating.noRating'
} as const satisfies Record<RmReviewRatingFilter, string>;

/** Reviews on one page of the inbox. */
export const REVIEWS_PAGE_SIZE = 20;

/** How long a loaded page of reviews counts as fresh, so moving the review sheet across pages sends one request. */
export const REVIEWS_STALE_TIME = 30_000;

/** Skeleton rows the table shows on the first load. */
export const REVIEWS_SKELETON_ROW_COUNT = 10;

/** Smallest width before the reviews table scrolls sideways: the design has no phone layout. */
export const REVIEWS_TABLE_MIN_WIDTH = '1000px';

/**
 * The review sites with their own DS icon, by directory identifier. Any other site shows the `Globe` icon.
 */
export const DIRECTORY_ICONS: Partial<Record<RmDirectoryIdentifier, ComponentType<SVGProps<SVGSVGElement>>>> = {
  google: GoogleOriginal,
  facebook: FacebookOriginal,
  yelp: YelpOriginal,
  trustpilot: TrustpilotOriginal
};

/** Label key and badge variant of each review status. */
export const REVIEW_STATUS_BADGES = {
  [RM_REVIEW_STATUS.UNREAD]: { labelKey: 'unread', variant: 'primary' },
  [RM_REVIEW_STATUS.ACTION_REQUIRED]: { labelKey: 'actionRequired', variant: 'primary' },
  [RM_REVIEW_STATUS.MANUALLY_RESPONDED]: { labelKey: 'manuallyResponded', variant: 'secondary' },
  [RM_REVIEW_STATUS.AUTO_RESPONDED]: { labelKey: 'autoResponded', variant: 'outline' },
  [RM_REVIEW_STATUS.DO_NOT_RESPOND]: { labelKey: 'doNotRespond', variant: 'outline' },
  [RM_REVIEW_STATUS.PENDING_REMOVAL]: { labelKey: 'pendingRemoval', variant: 'outline' },
  [RM_REVIEW_STATUS.SUCCESSFULLY_REMOVED]: { labelKey: 'successfullyRemoved', variant: 'secondary' },
  [RM_REVIEW_STATUS.REMOVAL_FAILED]: { labelKey: 'removalFailed', variant: 'destructive' }
} as const satisfies Record<RmReviewStatus, { labelKey: string; variant: NonNullable<BadgeProps['variant']> }>;

/**
 * The statuses a customer can set on a review, in the order the status dropdown lists them. Auto-replies and the
 * review-removal flow set the others, and a review in one of them can't be changed.
 */
export const SETTABLE_REVIEW_STATUSES = [...RMUpdateReviewSettingsSchema.shape.status.options] as const;

/**
 * The inbox's status tabs, in the order it shows them, as the `tab` search param writes them. All comes first, as
 * the first option of a filter does.
 */
export const REVIEW_TAB = {
  ALL: 'all',
  NEEDS_ACTION: 'needs_action',
  REPLIED: 'replied',
  SKIPPED: 'skipped'
} as const;

/** A status tab of the inbox. */
export type ReviewTab = (typeof REVIEW_TAB)[keyof typeof REVIEW_TAB];

/** The tab the inbox opens on; the page address leaves it out. */
export const DEFAULT_REVIEW_TAB = REVIEW_TAB.NEEDS_ACTION;

/**
 * Label key, empty-tab line key and searched statuses of each status tab. All searches every status, so the removal
 * statuses show only there, and it has no line of its own: an empty All means the report has no reviews. Any other tab
 * searches at least one status, as Find Reviews refuses an empty status list.
 */
export const REVIEW_TAB_CONFIG = {
  [REVIEW_TAB.ALL]: { labelKey: 'all', emptyKey: null, statuses: null },
  [REVIEW_TAB.NEEDS_ACTION]: {
    labelKey: 'needsAction',
    emptyKey: 'needsAction',
    statuses: [RM_REVIEW_STATUS.UNREAD, RM_REVIEW_STATUS.ACTION_REQUIRED]
  },
  [REVIEW_TAB.REPLIED]: {
    labelKey: 'replied',
    emptyKey: 'replied',
    statuses: [RM_REVIEW_STATUS.MANUALLY_RESPONDED, RM_REVIEW_STATUS.AUTO_RESPONDED]
  },
  [REVIEW_TAB.SKIPPED]: { labelKey: 'skipped', emptyKey: 'skipped', statuses: [RM_REVIEW_STATUS.DO_NOT_RESPOND] }
} as const satisfies Record<
  ReviewTab,
  { labelKey: string; emptyKey: string | null; statuses: readonly [RmReviewStatus, ...RmReviewStatus[]] | null }
>;

/**
 * The orders the inbox lists its reviews in, as the `sort` search param writes them. The Date header switches between
 * newest and oldest first, the Rating header between highest and lowest first.
 */
export const REVIEW_SORT = {
  NEWEST: 'newest',
  OLDEST: 'oldest',
  HIGHEST: 'highest',
  LOWEST: 'lowest'
} as const;

/** An order of the inbox. */
export type ReviewSort = (typeof REVIEW_SORT)[keyof typeof REVIEW_SORT];

/** The order the inbox opens in; the page address leaves it out. */
export const DEFAULT_REVIEW_SORT = REVIEW_SORT.NEWEST;

/** Ids of the table columns that sort; the other columns don't. */
export const SORTABLE_REVIEW_COLUMN = {
  RATING: 'rating',
  DATE: 'date'
} as const;

/** A table column that sorts. */
type SortableReviewColumn = (typeof SORTABLE_REVIEW_COLUMN)[keyof typeof SORTABLE_REVIEW_COLUMN];

// The fields and directions Find Reviews sorts by, as its `sort` param takes them
const { field: findReviewsSortField, order: findReviewsSortOrder } =
  RMFindReviewsParamsSchema.shape.sort.unwrap().shape;
const SORT_FIELD = findReviewsSortField.enum;
const SORT_ORDER = findReviewsSortOrder.unwrap().enum;

/**
 * The column and direction the table shows for each order, and the `sort` Find Reviews gets for it. Newest first sends
 * no sort, as it is Find Reviews' own order; any other order sends its direction too, since a field alone sorts
 * ascending.
 */
export const REVIEW_SORT_CONFIG = {
  [REVIEW_SORT.NEWEST]: { columnId: SORTABLE_REVIEW_COLUMN.DATE, isDesc: true, request: null },
  [REVIEW_SORT.OLDEST]: {
    columnId: SORTABLE_REVIEW_COLUMN.DATE,
    isDesc: false,
    request: { field: SORT_FIELD.published_at, order: SORT_ORDER.asc }
  },
  [REVIEW_SORT.HIGHEST]: {
    columnId: SORTABLE_REVIEW_COLUMN.RATING,
    isDesc: true,
    request: { field: SORT_FIELD.rating, order: SORT_ORDER.desc }
  },
  [REVIEW_SORT.LOWEST]: {
    columnId: SORTABLE_REVIEW_COLUMN.RATING,
    isDesc: false,
    request: { field: SORT_FIELD.rating, order: SORT_ORDER.asc }
  }
} as const satisfies Record<
  ReviewSort,
  {
    columnId: SortableReviewColumn;
    isDesc: boolean;
    request: Required<NonNullable<RMFindReviewsParams['sort']>> | null;
  }
>;

/** GTM `eventAction` of `top_action_clicked`: which page header control the user clicked. */
export const REVIEW_MANAGER_TOP_ACTION = {
  UPDATE_DATA: 'update_data',
  REPLY_TEMPLATES: 'reply_templates'
} as const;

/** GTM `eventAction` of `top_action_clicked`. */
export type ReviewManagerTopAction = (typeof REVIEW_MANAGER_TOP_ACTION)[keyof typeof REVIEW_MANAGER_TOP_ACTION];

/** GTM `eventAction` of `filter_changed`: which filter the user changed. */
export const REVIEW_MANAGER_FILTER_ACTION = {
  SOURCE: 'source',
  RATING: 'rating'
} as const;

/** GTM `eventAction` of `filter_changed`. */
export type ReviewManagerFilterAction =
  (typeof REVIEW_MANAGER_FILTER_ACTION)[keyof typeof REVIEW_MANAGER_FILTER_ACTION];

/** GTM `eventAction` of `filters_cleared`: the toolbar's Clear all, or Clear filters in the no-matches state. */
export const FILTERS_CLEARED_ACTION = {
  TOOLBAR: 'toolbar',
  NO_MATCHES: 'no_matches'
} as const;

/** GTM `eventAction` of `filters_cleared`. */
export type FiltersClearedAction = (typeof FILTERS_CLEARED_ACTION)[keyof typeof FILTERS_CLEARED_ACTION];

/** GTM `eventAction` of `review_opened`: a click on the review's row, or Next or Previous in the review sheet. */
export const REVIEW_OPENED_ACTION = {
  ROW: 'row',
  NEXT: 'next',
  PREVIOUS: 'previous'
} as const;

/** GTM `eventAction` of `review_opened`. */
export type ReviewOpenedAction = (typeof REVIEW_OPENED_ACTION)[keyof typeof REVIEW_OPENED_ACTION];

/** Where the customer changed a review's status. */
export const STATUS_CHANGE_ORIGIN = {
  /** The status dropdown in a row of the reviews table. */
  ROW: 'row',
  /** The status dropdown in the review sheet. */
  PANEL: 'panel',
  /** Skip reply in the review sheet. */
  SKIP: 'skip'
} as const;

/** Where the customer changed a review's status. */
export type StatusChangeOrigin = (typeof STATUS_CHANGE_ORIGIN)[keyof typeof STATUS_CHANGE_ORIGIN];

/** A status dropdown: in a row of the reviews table, or in the review sheet. */
export type StatusDropdownOrigin = Exclude<StatusChangeOrigin, typeof STATUS_CHANGE_ORIGIN.SKIP>;

/** GTM `eventDescription` of `status_changed`: the dropdown the status was picked in. A skip sends `review_skipped`. */
export const STATUS_CHANGED_DESCRIPTION = {
  [STATUS_CHANGE_ORIGIN.ROW]: 'row',
  [STATUS_CHANGE_ORIGIN.PANEL]: 'panel'
} as const satisfies Record<StatusDropdownOrigin, string>;

/** GTM `eventLabel` naming Review Manager in the shared report failure events. */
export const REVIEW_MANAGER_TRACKING_TOOL = 'review_manager';
